import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
    Button,
    Icon,
    ModalDialog,
    ModalDialogBody,
    ModalDialogButtons,
    ModalDialogContent,
    ModalDialogFooter,
    Spinner,
    SwanIconKey,
    responsive,
    useScreenClass,
} from '@vp/swan';
import type { Dispatch, SetStateAction } from 'react';
import { __ } from '@99designs/i18n';
import { useProductOptions } from '../../../components/BriefForm/FormFields/ClientConfiguredFields/ProductOptions';
import { useBriefContext } from '../../BriefContext';
import { FieldGroupData } from '../FieldGroup';
import { useUpdateBriefMutation } from '../brief.generated';
import { transformBriefFormData } from '../getSubmit';

export function useEditState(
    group: FieldGroupData,
    setGroupFormKey: Dispatch<SetStateAction<number>>,
    briefId: string
) {
    const [isOpen, setIsOpen] = useState(false);
    const form = useFormContext();
    const [savedFormValue, setSavedFormValue] = useState(form.getValues());
    const setError = form?.setError;
    const { updateProduct, product } = useBriefContext();
    const [savedProduct, setSavedProduct] = useState(product);
    const { selectedOptions } = useProductOptions();
    const [savedProductOptions, setSavedProductOptions] = useState<Record<string, string>>();

    useEffect(() => {
        if (isOpen) {
            setSavedProductOptions(selectedOptions);
            setSavedFormValue(form.getValues());
        }
    }, [isOpen]);

    const [updateBrief, { loading }] = useUpdateBriefMutation({
        update: (cache, { data }) => {
            if (!data) {
                return;
            }
            if (data.brief.errors) {
                data.brief.errors.forEach((error) => {
                    setError(error.field, { type: 'server', message: error.reason });
                });
            }
            if (data.brief.dynamicFields) {
                cache.modify({
                    id: cache.identify(data.brief),
                    fields: {
                        dynamicFields: () => data.brief.dynamicFields,
                    },
                });
            }
        },
    });

    const onSubmit = () => {
        updateProduct(savedProduct);
        return updateBrief({
            variables: {
                id: briefId,
                fields: {
                    ...transformBriefFormData({
                        ...savedFormValue,
                        productOptions: savedProductOptions,
                    }),
                },
            },
        });
    };

    const open = () => {
        setIsOpen(true);
    };

    const cancel = async () => {
        await onSubmit();

        Object.keys(savedFormValue).forEach((key) => {
            const value = savedFormValue[key];
            form.setValue(key, value);
        });

        // sleep for 0.3 seconds to address bug in POUI, see: https://vistaprint.slack.com/archives/C057FQY14CT/p1740448662286579
        if (savedProductOptions) {
            const isCustomSizeChanged =
                savedProductOptions['Custom Height'] !== selectedOptions['Custom Height'] ||
                savedProductOptions['Custom Width'] !== selectedOptions['Custom Width'];

            if (isCustomSizeChanged) {
                await new Promise((resolve) => setTimeout(resolve, 300));
            }
        }

        setGroupFormKey((prev) => prev + 1);

        setIsOpen(false);
    };

    const close = async () => {
        const isValid = await form.trigger(group.fields.map((f) => f.id));
        if (!isValid) {
            return;
        }

        setIsOpen(false);
        setSavedProduct(product);
    };
    return { isOpen, isSaving: loading, open, cancel, close };
}

const ResponsiveModalDialogContent = responsive(ModalDialogContent);

export function EditFormGroupModal({
    briefId,
    showEditButton,
    editButtonIcon,
    groupHasErrors,
    setGroupFormKey,
    group,
    children,
}: {
    briefId: string;
    showEditButton: boolean;
    groupHasErrors: boolean;
    group: FieldGroupData;
    editButtonIcon: SwanIconKey;
    setGroupFormKey: Dispatch<SetStateAction<number>>;
    children: React.ReactNode;
}) {
    const screenClass = useScreenClass();
    const { isOpen, isSaving, open, cancel, close } = useEditState(group, setGroupFormKey, briefId);
    const { trigger } = useFormContext();
    const { isUpdatingProduct: isUpdating } = useBriefContext();
    const dialogRef = React.useRef<HTMLDialogElement>(null);

    // Prevents the modal from closing when clicking outside the modal
    useEffect(() => {
        if (dialogRef.current) {
            dialogRef.current.addEventListener(
                'click',
                (e) => {
                    if (e.target === e.currentTarget) {
                        e.stopImmediatePropagation();
                    }
                },
                true
            );
        }
    }, []);

    useEffect(() => {
        if (!isOpen) trigger();
    }, [trigger, isOpen]);

    return (
        <>
            <ModalDialog
                ref={dialogRef}
                isOpen={isOpen}
                onRequestDismiss={() => null}
                onlyRenderWhenOpen
            >
                <ResponsiveModalDialogContent
                    aria-labelledby="modal dialog general"
                    lg={{ style: { minWidth: '800px' } }}
                    sm={{ style: { minWidth: '600px' } }}
                    paddingBottom={0}
                >
                    <ModalDialogBody>{children}</ModalDialogBody>
                    <ModalDialogFooter
                        style={{
                            position: 'sticky',
                            height: '94px',
                            bottom: 0,
                            backgroundColor: 'white',
                            zIndex: 1000,
                        }}
                    >
                        <ModalDialogButtons>
                            <Button skin="secondary" disabled={isUpdating} onClick={cancel}>
                                {isSaving && <Spinner accessibleText={__('Loading...')} />}{' '}
                                {__('Cancel')}
                            </Button>
                            <Button
                                disabled={groupHasErrors || isUpdating}
                                skin="primary"
                                onClick={close}
                            >
                                {__('Save')}
                            </Button>
                        </ModalDialogButtons>
                    </ModalDialogFooter>
                </ResponsiveModalDialogContent>
            </ModalDialog>
            {showEditButton && (
                <Button
                    data-testid={`edit-button-${group.id}`}
                    style={
                        screenClass === 'xs'
                            ? {
                                  marginLeft: 'auto',
                                  position: 'absolute',
                                  top: '4px',
                                  right: '4px',
                                  width: '44px',
                                  height: '44px',
                              }
                            : {
                                  alignSelf: 'flex-start',
                                  marginLeft: 'auto',
                                  width: '44px',
                                  height: '44px',
                              }
                    }
                    skin={'tertiary'}
                    onClick={open}
                >
                    <Icon iconType={editButtonIcon} />
                </Button>
            )}
        </>
    );
}
